import {
  TRUNK_ZERO_COUNTRY_CODES,
  formatPhoneNumberToE123,
  normalizePhoneNumberToE164,
} from 'src/app/features/contact/utils/phone-number-normalization';
import { PhoneNumberDisplayFormat } from 'src/core/models/settings/localization/phone-number-display-format.enum';

/**
 * Renders a phone number according to the tenant's `PhoneNumberDisplayFormat`.
 *
 * Parsing and grouping are delegated to the contact module's phone utilities — the same ones the
 * contact form uses to normalise what an operator types — so a number is displayed exactly the way
 * it is stored and validated, with the tenant setting choosing only *which* of the three renderings
 * is shown. This is display only: it never rewrites a form value.
 *
 * A value that is not a valid E.164 number (an internal extension, a service number, free text) is
 * echoed unchanged rather than regrouped into something misleading.
 */
export function formatPhoneNumber(value: string, format: PhoneNumberDisplayFormat): string {
  const trimmed = value?.trim() ?? '';

  const e164 = normalizePhoneNumberToE164(trimmed);
  if (!e164) {
    return trimmed;
  }

  if (format === PhoneNumberDisplayFormat.DigitsOnly) {
    return e164.slice(1);
  }

  const international = formatPhoneNumberToE123(e164);

  if (format === PhoneNumberDisplayFormat.Local) {
    return toLocalNumber(e164, international);
  }

  return international;
}

/**
 * Rewrites `+90 532 123 45 67` as `0532 123 45 67`, reusing the international grouping.
 *
 * Only countries that actually use a national trunk `0` are converted; for the rest (North America,
 * for instance) a leading zero would be wrong, so the international form is kept.
 */
function toLocalNumber(e164: string, international: string): string {
  const digits = e164.slice(1);

  const countryCode = TRUNK_ZERO_COUNTRY_CODES.find(code => digits.startsWith(code));
  if (!countryCode) {
    return international;
  }

  const prefix = `+${countryCode} `;
  if (!international.startsWith(prefix)) {
    return international;
  }

  return `0${international.slice(prefix.length)}`;
}
